import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFirebaseUser } from '@/hooks/useFirebaseUser'
import { uploadToStorage } from '@/integrations/firebase'
import { WorkOrderErrandResponse } from '../client'
import {
  deleteV1WorkOrderErrandsByIdMutation,
  getV1WorkOrderErrandsOptions,
  getV1WorkOrderErrandsQueryKey,
  patchV1WorkOrderErrandsByIdMutation,
  postV1WorkOrderErrandsMutation,
} from '../client/@tanstack/react-query.gen'
import { postV1WorkOrderErrandsUploadUrls } from '../client/sdk.gen'

export type WorkOrderErrand = WorkOrderErrandResponse

export const useWorkOrderErrands = (workOrderId: string, options?: { enabled?: boolean }) => {
  const { isLoggedIn } = useFirebaseUser()
  const query = useQuery({
    ...getV1WorkOrderErrandsOptions({ query: { limit: 0, page: 1, workOrderId } }),
    enabled: isLoggedIn && (options?.enabled ?? true),
  })

  return {
    ...query,
    data: query.data?.docs ?? [],
  }
}

export const useCreateWorkOrderErrand = (workOrderId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...postV1WorkOrderErrandsMutation(),
    // Caller passes a per-call `onError` with a specific message — skip the generic global toast.
    meta: { skipGlobalErrorToast: true },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getV1WorkOrderErrandsQueryKey({
          query: { limit: 0, page: 1, workOrderId },
        }),
        exact: false,
      }),
  })
}

export const useUpdateWorkOrderErrand = (workOrderId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...patchV1WorkOrderErrandsByIdMutation(),
    // Caller passes a per-call `onError` with a specific message — skip the generic global toast.
    meta: { skipGlobalErrorToast: true },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getV1WorkOrderErrandsQueryKey({
          query: { limit: 0, page: 1, workOrderId },
        }),
        exact: false,
      }),
  })
}

export const useDeleteWorkOrderErrand = (workOrderId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...deleteV1WorkOrderErrandsByIdMutation(),
    // Caller passes a per-call `onError` with a specific message — skip the generic global toast.
    meta: { skipGlobalErrorToast: true },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getV1WorkOrderErrandsQueryKey({
          query: { limit: 0, page: 1, workOrderId },
        }),
        exact: false,
      }),
  })
}

// Returns {path, name}[] for the caller to include in the create/update body.
// Does NOT auto-persist — images are saved when the errand form is submitted.
export const useUploadErrandImages = (workOrderId: string) => {
  return useMutation({
    mutationFn: async (files: File[]) => {
      if (files.length === 0) return []

      const { data, error } = await postV1WorkOrderErrandsUploadUrls({
        body: { workOrderId, files: files.map(f => ({ name: f.name })) },
      })
      if (error || !data) throw new Error('Failed to get upload URLs')

      return Promise.all(
        files.map(async (file, idx) => {
          const path = data.paths[idx]
          if (!path) throw new Error('Missing upload path for file')
          await uploadToStorage(path, file)
          return { path, name: file.name }
        }),
      )
    },
  })
}
